/**
 * シフト提出(2026-09-12 新設)。本人が「その月のどの日に何時から何時まで入れるか」を出す。
 *  - operator: 自分の分。スマートフォン前提
 *  - admin: 対象者を選んで代理入力・確認ができる
 * ここで出した時間が「提出シフト時間」。管理者が出勤表で直した結果が「修正シフト時間」になる。
 * 2026-09-20 UIデザインシステムへ移行(D-111): 月の切替と対象者は FilterBar、枠は Panel、
 * 表は `.report-scroll` ＋ `.report-table`(日付の列を左に固定)、数字は formatters。スマートフォンでも崩れないようにする。
 * 2026-09-22 休憩・中抜け・備考を追加(D-126): 休憩は15分単位で60分まで(6時間以上なら60分が既定)、
 * 中抜けは1時間から30分単位、**中抜けを入れた日は「何時から何時まで」を備考に書かないと提出できない**。
 * 2026-09-22 提出したものを確定とする(D-130): 本人は提出したら直せない(変更申請から)。
 * 管理者が提出済みの月を直して保存すると、変更履歴に足し、時間を変えた日は本人にお知らせを出す。
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '@core/auth/AuthProvider';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { dayColorOf } from '@core/lib/holidays';
import { WORK_STYLE_LABELS, type DateStr, type MonthStr, type Operator, type WorkStyle } from '@core/lib/types';
import { fetchOperatorById, fetchOperators } from '@core/masters/operatorRepo';
import { addMonths, daysInMonth } from '@core/dashboard/calc';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import FilterBar from '@core/components/ui/FilterBar';
import Panel from '@core/components/ui/Panel';
import { formatDecimal } from '@core/utils/formatters';
import {
  BREAK_MINUTES,
  GAP_TIMES,
  gapHoursOf,
  hhmmOf,
  breakMinOf,
  dayHours,
  fetchSubmission,
  missingGapNotes,
  normalizeDays,
  saveSubmission,
  submissionId,
  submittedHours,
  withTimes,
  type ShiftChangeLog,
  type ShiftSubmission,
  type SubmitDay,
} from './shiftSubmitRepo';
import { logTextOf } from './shiftDisplay';
import { changedDaysOf, saveSubmissionByAdmin, styleResolver } from './shiftNoticeRepo';

/** 8:00〜19:00 の30分刻み(2026-09-12 指示) */
const hours = Array.from({ length: 23 }, (_, i) => 8 + i * 0.5);
const hhmm = (h: number) => `${Math.floor(h)}:${h % 1 ? '30' : '00'}`;

export default function ShiftSubmitTab() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [month, setMonth] = useState<MonthStr>(todayStr().slice(0, 7) as MonthStr);
  const [opId, setOpId] = useState(user?.op_id ?? '');
  const [operators, setOperators] = useState<Operator[]>([]);
  const [days, setDays] = useState<Record<string, SubmitDay>>({});
  const [note, setNote] = useState('');
  const [bbId, setBbId] = useState('');
  const [me, setMe] = useState<Operator | null>(null);
  /** すでに提出済みか(operator はここから先、勝手に変えられない。変更は申請から。D-113) */
  const [submitted, setSubmitted] = useState(false);
  /** 保存済みの提出(管理者が直したときの 変更履歴・お知らせの「変更前」に使う) */
  const [saved, setSaved] = useState<ShiftSubmission | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const savedRef = useRef('');
  const guard = useUnsavedGuard(dirty);

  useEffect(() => {
    if (isAdmin) {
      void fetchOperators()
        .then(setOperators)
        .catch(() => undefined);
    } else if (user?.op_id) {
      // operator は一覧を読めないので自分のドキュメントだけ読む
      void fetchOperatorById(user.op_id)
        .then(setMe)
        .catch(() => undefined);
    }
  }, [isAdmin, user?.op_id]);

  const load = useCallback(async () => {
    if (!opId) {
      setDays({});
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const s = await fetchSubmission(month, opId);
      setSubmitted(s !== null);
      setSaved(s);
      const next = s?.days ?? {};
      setDays(next);
      setNote(s?.note ?? '');
      setBbId(s?.bb_id ?? '');
      savedRef.current = JSON.stringify(next);
      setDirty(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [month, opId, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (day: string, v: SubmitDay | null) => {
    setDays((prev) => {
      const next = { ...prev };
      if (v === null) delete next[day];
      else next[day] = v;
      setDirty(JSON.stringify(next) !== savedRef.current);
      return next;
    });
  };

  const total = useMemo(() => submittedHours(days), [days]);
  /** 本人が勝手に直せない状態(提出済みの月)。管理者はいつでも直せる */
  const locked = !isAdmin && submitted;
  const target = useMemo(() => (isAdmin ? (operators.find((o) => o.id === opId) ?? null) : me), [isAdmin, operators, opId, me]);
  const name = target?.name ?? user?.name ?? '';
  /**
   * 列は **出勤形態ごと**(2026-09-12 指示)。同じ出勤形態のBB IDが複数あっても1列にまとめる。
   * 品川と在宅の2つを持つ人だけ2列になる。報酬と BB ID は出さない
   */
  const styleCols = useMemo(() => {
    const seen = new Map<WorkStyle, string>();
    for (const a of target?.bb_accounts ?? []) if (!seen.has(a.work_style)) seen.set(a.work_style, a.bb_id);
    return [...seen.entries()].map(([ws, bb]) => ({ ws, bb }));
  }, [target]);
  /** BB ID → 出勤形態(その日の入力がどの列のものかを見分ける) */
  const wsOfBb = useMemo(() => new Map((target?.bb_accounts ?? []).map((a) => [a.bb_id, a.work_style])), [target]);
  const accounts = target?.bb_accounts ?? [];
  // 出勤形態×報酬区分は列で分ける。保存する代表値は1つ目(集計の互換用)
  useEffect(() => {
    if (!bbId && accounts.length >= 1) setBbId(accounts[0]?.bb_id ?? '');
  }, [bbId, accounts]);

  /** 中抜けがあるのに備考が空の日(提出を止めて、その日の備考欄を赤くする) */
  const noteMissing = useMemo(() => new Set(missingGapNotes(days)), [days]);

  const save = async () => {
    if (!user || !opId) return;
    if (noteMissing.size > 0) {
      toast(`中抜けがある日は備考に「何時から何時まで中抜けするか」を書いてください(${[...noteMissing].map((d) => `${Number(d)}日`).join('・')})`);
      return;
    }
    setSaving(true);
    try {
      const next = normalizeDays(days);
      let logs: ShiftChangeLog[] = [];
      if (isAdmin && saved) {
        // 管理者が提出済みの月を直す: 直した日を変更履歴に足し、時間を変えた日は本人にお知らせ(D-130)
        const by = user.name || user.email || user.id;
        const at = new Date().toISOString();
        const changed = changedDaysOf(saved.days, next);
        logs = [
          ...changed.map((day) => ({ day, before: logTextOf(saved.days[day]), after: logTextOf(next[day]), by, at, before_hours: dayHours(saved.days[day]) })),
          ...(saved.logs ?? []),
        ];
        const noticeDays = await saveSubmissionByAdmin({
          month,
          opId,
          name,
          bbId,
          days: next,
          note,
          userId: user.id,
          logs,
          prevDays: saved.days,
          byName: by,
          silent: !!user.op_id && user.op_id === opId,
          styleOf: styleResolver(operators),
        });
        toast(changed.some((d) => noticeDays.includes(d)) ? `保存しました。${name} さんにお知らせを出しました` : '保存しました');
      } else {
        await saveSubmission({ month, opId, name, bbId, days: next, note, userId: user.id });
        toast('シフトを提出しました');
      }
      setSaved({ id: submissionId(month, opId), month, op_id: opId, name, bb_id: bbId, days: next, note: note.trim(), logs });
      setSubmitted(true);
      setDays(next);
      savedRef.current = JSON.stringify(next);
      setDirty(false);
    } catch (e) {
      toast(e instanceof Error ? e.message : '提出に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const list = Array.from({ length: daysInMonth(month) }, (_, i) => String(i + 1).padStart(2, '0'));

  return (
    <>
      {guard.modal}

      <FilterBar label="提出する月">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1) as MonthStr)} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value as MonthStr)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1) as MonthStr)} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="ui-period-label">
          {month.slice(0, 4)}年{Number(month.slice(5))}月
        </span>
        {isAdmin && (
          <select className="ui-input" aria-label="スタッフ" value={opId} onChange={(e) => setOpId(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">スタッフを選ぶ</option>
            {operators
              .filter((o) => o.status === 'active')
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}({o.op_code})
                </option>
              ))}
          </select>
        )}
      </FilterBar>

      <Panel
        title="シフト提出"
        meta={opId ? `${name} ／ 提出シフト時間 ${formatDecimal(total, { digits: 2 })}h(休憩・中抜けを引いた時間)` : undefined}
        actions={
          <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" disabled={!opId || !dirty || saving || locked} onClick={() => void save()}>
            {isAdmin && submitted ? (saving ? '保存中…' : '保存する') : saving ? '提出中…' : '提出する'}
          </button>
        }
        note={
          locked
            ? 'この月はすでに提出済みです。**変更したいときは「変更申請」から、理由を書いて申請してください**(管理者が承認すると反映されます)'
            : isAdmin && submitted
              ? 'この月は提出済みです。**直して保存すると、時間を変えた日は本人にお知らせが出ます**(変更履歴にも残ります)'
              : '入れる日だけ開始と終了を選びます。休みの日は「休み」のままにしてください'
        }
      >
        {!opId ? (
          <div className="ui-empty">{isAdmin ? 'スタッフを選んでください' : 'アカウントにアポインターが紐づいていません。管理者に連絡してください。'}</div>
        ) : loading ? (
          <div className="ui-empty">読み込み中…</div>
        ) : (
          <>
            <div className="report-scroll is-matrix">
              <table className="report-table no-stack shift-submit">
                <thead>
                  <tr>
                    <th scope="col" rowSpan={2} className="is-sticky is-sticky-edge" style={{ left: 0, width: 82, minWidth: 82 }}>
                      日付
                    </th>
                    {styleCols.map((sc, i) => (
                      <th key={sc.ws} scope="col" colSpan={2} className={`is-center ws-col ws-${i % 2}`} style={{ minWidth: 190 }}>
                        {WORK_STYLE_LABELS[sc.ws]}
                      </th>
                    ))}
                    <th scope="col" rowSpan={2} className="is-center col-group" style={{ width: 90 }} title="15分単位・60分まで。6時間以上の日は60分が既定です">
                      休憩
                    </th>
                    <th scope="col" rowSpan={2} className="is-center" style={{ width: 90 }} title="1時間から30分単位">
                      中抜け
                    </th>
                    <th scope="col" rowSpan={2} style={{ minWidth: 200 }} title="中抜けがある日は「何時から何時まで」を必ず書いてください">
                      備考
                    </th>
                    <th scope="col" rowSpan={2} className="is-num" style={{ width: 64 }}>
                      時間
                    </th>
                  </tr>
                  <tr>
                    {styleCols.map((sc, i) => (
                      <Fragment key={sc.ws}>
                        <th scope="col" className={`is-center ws-col ws-${i % 2}`} style={{ width: 95 }}>開始</th>
                        <th scope="col" className={`is-center ws-col ws-${i % 2}`} style={{ width: 95 }}>終了</th>
                      </Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((d) => {
                    const date = `${month}-${d}` as DateStr;
                    const v = days[d];
                    const dow = weekdayLabel(date);
                    const c = dayColorOf(date);
                    const cls = c === 'red' ? 'day-sun' : c === 'blue' ? 'day-sat' : '';
                    return (
                      <tr key={d}>
                        <th scope="row" className={`is-sticky is-sticky-edge ${cls}`} style={{ left: 0 }}>
                          {Number(d)}日{dow}
                        </th>
                        {styleCols.map((sc, i) => {
                          // その日に入っているのはこの出勤形態か(1日1つだけ)
                          const curWs = v ? (wsOfBb.get(v.bb_id ?? '') ?? styleCols[0]?.ws) : undefined;
                          const mine = v && curWs === sc.ws ? v : undefined;
                          return (
                            <Fragment key={sc.ws}>
                              <td className={`ws-col ws-${i % 2}`}>
                                <select
                                  className="ui-input"
                                  aria-label={`${Number(d)}日 ${WORK_STYLE_LABELS[sc.ws]} の開始`}
                                  disabled={locked}
                                  value={mine?.start ?? ''}
                                  onChange={(e) =>
                                    patch(
                                      d,
                                      e.target.value === ''
                                        ? null
                                        : { ...withTimes(mine, Number(e.target.value), mine?.end ?? Math.min(19, Number(e.target.value) + 1)), bb_id: sc.bb },
                                    )
                                  }
                                  style={{ width: '100%' }}
                                >
                                  <option value="">休み</option>
                                  {hours.map((h) => (
                                    <option key={h} value={h}>
                                      {hhmm(h)}
                                    </option>
                                  ))}
                                </select>
                              </td>
                              <td className={`ws-col ws-${i % 2}`}>
                                <select
                                  className="ui-input"
                                  aria-label={`${Number(d)}日 ${WORK_STYLE_LABELS[sc.ws]} の終了`}
                                  value={mine?.end ?? ''}
                                  disabled={locked || !mine}
                                  onChange={(e) => mine && patch(d, { ...withTimes(mine, mine.start, Number(e.target.value)), bb_id: sc.bb })}
                                  style={{ width: '100%' }}
                                >
                                  <option value="">—</option>
                                  {hours.map((h) => (
                                    <option key={h} value={h}>
                                      {hhmm(h)}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            </Fragment>
                          );
                        })}
                        {/* 休憩(分)。未入力なら 6時間以上で60分・それ未満は0分 */}
                        <td className="col-group">
                          <select
                            className="ui-input"
                            aria-label={`${Number(d)}日 の休憩`}
                            disabled={locked || !v}
                            value={v ? breakMinOf(v) : ''}
                            onChange={(e) => v && patch(d, { ...v, break_min: Number(e.target.value) })}
                            style={{ width: '100%' }}
                          >
                            {!v && <option value="">—</option>}
                            {BREAK_MINUTES.map((m) => (
                              <option key={m} value={m}>
                                {m === 0 ? 'なし' : `${m}分`}
                              </option>
                            ))}
                          </select>
                        </td>
                        {/* 中抜け: 何時から何時まで(時間は自動。2026-09-25 発注者指示) */}
                        <td>
                          <span className="gap-pick">
                            <select
                              className="ui-input"
                              aria-label={`${Number(d)}日 の中抜け開始`}
                              disabled={locked || !v}
                              value={v?.gap_from ?? ''}
                              onChange={(e) => {
                                if (!v) return;
                                const from = e.target.value === '' ? undefined : Number(e.target.value);
                                const to = from === undefined ? undefined : (v.gap_to !== undefined && v.gap_to > from ? v.gap_to : from + 1);
                                patch(d, { ...v, gap_from: from, gap_to: to, gap_hours: from === undefined || to === undefined ? undefined : Math.round((to - from) * 2) / 2 });
                              }}
                            >
                              <option value="">なし</option>
                              {GAP_TIMES.map((h) => (
                                <option key={h} value={h}>
                                  {hhmmOf(h)}
                                </option>
                              ))}
                            </select>
                            <span className="gap-sep">〜</span>
                            <select
                              className="ui-input"
                              aria-label={`${Number(d)}日 の中抜け終了`}
                              disabled={locked || !v || v.gap_from === undefined}
                              value={v?.gap_to ?? ''}
                              onChange={(e) => {
                                if (!v || v.gap_from === undefined) return;
                                const to = e.target.value === '' ? undefined : Number(e.target.value);
                                patch(d, { ...v, gap_to: to, gap_hours: to === undefined ? undefined : Math.round((to - v.gap_from!) * 2) / 2 });
                              }}
                            >
                              <option value="">—</option>
                              {GAP_TIMES.filter((h) => v?.gap_from === undefined || h > v.gap_from).map((h) => (
                                <option key={h} value={h}>
                                  {hhmmOf(h)}
                                </option>
                              ))}
                            </select>
                            <span className="gap-hours">{v && gapHoursOf(v) > 0 ? `${gapHoursOf(v)}h` : ''}</span>
                          </span>
                        </td>
                        {/* 備考。中抜けがある日は必須 */}
                        <td>
                          <input
                            className="ui-input"
                            aria-label={`${Number(d)}日 の備考`}
                            aria-invalid={noteMissing.has(d) || undefined}
                            disabled={locked || !v}
                            value={v?.note ?? ''}
                            placeholder={v && gapHoursOf(v) > 0 ? '例: 通院のため(必須)' : ''}
                            onChange={(e) => v && patch(d, { ...v, note: e.target.value })}
                            style={{ width: '100%' }}
                          />
                        </td>
                        {/* 時間 = (終了 − 開始) − 休憩 − 中抜け(D-127) */}
                        <td className="is-num">{v ? formatDecimal(dayHours(v), { digits: 2 }) : ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className="ui-field-inline" style={{ marginTop: 'var(--space-3)', alignItems: 'flex-start' }}>
              <label className="ui-hint" htmlFor="shift-note">
                連絡事項(任意)
              </label>
              <textarea
                id="shift-note"
                className="ui-input"
                disabled={locked}
                value={note}
                onChange={(e) => {
                  setNote(e.target.value);
                  setDirty(true);
                }}
                rows={2}
                style={{ width: '100%', maxWidth: 560 }}
              />
            </div>
          </>
        )}
      </Panel>
    </>
  );
}
